/** @jsxRuntime classic */

/** @jsx jsx */
import { jsx } from "@emotion/core";
import { LoaderLayout } from "components/atoms/loader/LoaderLayout";
import { AccountReceipt } from "containers/account/receipt/AccountReceipt";
import { useI18n } from "hooks/i18n/i18n";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { SubContentsLayout } from "ui/SubContentsLayout";

import { OrderInfo } from "./components/OrderInfo";
import { ProductActionButtons } from "./components/ProductActionButtons";
import { ProductInfo } from "./components/ProductInfo";
import { PRODUCT_TYPE } from "./constants/ProductType";
import { TICKET_CLASS } from "./constants/TicketClass";
import {
  containerStyle,
  contentWrapperStyle,
  informationStyle,
  titleStyle,
} from "./styles";

import type { DeliveryData } from "./types/DeliveryData";
import type { FulfillmentData } from "./types/FulfillmentData";
import type { OrderData } from "./types/OrderData";
import type { ProductItem } from "./types/ProductItem";
import type { ActiveTransaction } from "@spwn/types/firebase/firestore";
import type { ReceiptData } from "containers/account/receipt/AccountReceipt";
import type { FC, ReactNode } from "react";

type Props = {
  deliveryData: DeliveryData | null;
  fulfillmentData: FulfillmentData | null;
  handleCloseReceipt: () => void;
  handleContinuePayment: () => void;
  handleCopyOrderId: (orderId: number) => void;
  handleShowReceipt: () => void;
  isDisplayReceipt: boolean;
  isLoadingAddress: boolean;
  isLoadingBonus: boolean;
  isLoadingFulfillment: boolean;
  isPurchased: boolean;
  orderData: OrderData | null;
  orderList: ProductItem[];
  receiptData: ReceiptData;
  renderConfirmCvsPayment: () => ReactNode;
  transaction?: ActiveTransaction;
};

export const View: FC<Props> = (props) => {
  const {
    deliveryData,
    fulfillmentData,
    handleCloseReceipt,
    handleContinuePayment,
    handleCopyOrderId,
    handleShowReceipt,
    isDisplayReceipt,
    isLoadingAddress,
    isLoadingBonus,
    isLoadingFulfillment,
    isPurchased,
    orderData,
    orderList,
    receiptData,
    renderConfirmCvsPayment,
    transaction,
  } = props;

  const { t } = useI18n();

  if (isDisplayReceipt) {
    return (
      <AccountReceipt
        receiptData={receiptData}
        resetState={handleCloseReceipt}
      />
    );
  }

  if (!orderData) {
    return <LoaderLayout />;
  }

  const isLiveStream = (item: ProductItem) => {
    if (item.type !== PRODUCT_TYPE.TICKET) return false;
    return (
      item.liveTicketInfo.class === TICKET_CLASS.LIVE_STREAM ||
      item.liveTicketInfo.class === TICKET_CLASS.VOD_STREAM
    );
  };

  return (
    <SubContentsLayout bgcolor="GRAY">
      {renderConfirmCvsPayment()}
      <BreadcrumbNavigation
        breadcrumbs={[
          { label: t("common.routes.home"), link: "/" },
          { label: t("common.routes.account"), link: "/account" },
          { label: t("settlementHistory.breadcrumb.orderDetails"), link: null },
        ]}
      />
      <div css={containerStyle}>
        <div css={contentWrapperStyle}>
          <h1 css={titleStyle}>{t("settlementHistory.pageTitle")}</h1>
          <div css={informationStyle}>
            <OrderInfo
              deliveryData={deliveryData ?? undefined}
              fulfillmentData={fulfillmentData ?? undefined}
              isLoadingAddress={isLoadingAddress}
              isLoadingFulfillment={isLoadingFulfillment}
              isPurchased={isPurchased}
              onContinuePayment={handleContinuePayment}
              onCopyOrderId={handleCopyOrderId}
              onShowReceipt={handleShowReceipt}
              orderData={orderData}
              transaction={transaction}
            />
            {orderList.map((item) => (
              <div key={`${item.productName}-${item.variationName}`}>
                <ProductInfo {...item} />
                {item.type === PRODUCT_TYPE.TICKET && (
                  <ProductActionButtons
                    eventId={item.liveTicketInfo.eventId}
                    hasBonusMedia={item.liveTicketInfo.hasBonusMedia}
                    isLiveStreaming={isLiveStream(item)}
                    isLoadingBonus={isLoadingBonus}
                    isPurchased={isPurchased}
                    orderId={orderData.orderId}
                    ticketId={item.liveTicketInfo.ticketId}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </SubContentsLayout>
  );
};
